import RestaurantPreview from '@discover-components/city/restaurantPreview';
import {
  useState,
  useCallback,
  useMemo,
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import AvailabilityOptionsDialogWithBooking from '@discover-overrides/components/availability/options/dialog';
import useDebounce from '@discover-lib/useDebounce';
import { cn } from '@discover-lib/utils';
import usePrevious from '@discover-lib/usePrevious';
import { type RestaurantTagType } from '@discover-data/stores/restaurant/gql/fragments/restaurantTag';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { type PeopleOption } from '@discover-data/stores/hooks/people/options';
import useRestaurants from '@discover-data/stores/hooks/useRestaurants';
import {
  completeListInitialLoad,
  subscribeListInitialLoadReady,
} from '@discover-data/stores/restaurant/utils/store/listPerformance';
import usePage from '@discover-data/stores/hooks/page/usePage';
import usePeople from '@discover-data/stores/hooks/people/usePeople';
import useSort from '@discover-data/stores/hooks/sort/useSort';
import useSuburbs from '@discover-data/stores/hooks/suburb/useSuburbs';
import { useStoreRehydrated, useStoreState } from '@discover-data/stores/restaurant/hooks';
import { RestaurantCardSkeleton } from '@discover-components/restaurant/card';
import {
  AvailabilityOptionsDialogContext,
  type RegularAvailabilitySlotWithNonEmptyAvailabilityOptions,
} from '@discover-components/availability/options/dialog';
import DesktopSideBar from '@discover-components/restaurant/filters/desktopSideBar';
import {
  RestaurantFiltersDesktopTopBar,
  RestaurantFiltersMobileTopBar,
} from '@discover-components/restaurant/filters/topBar';
import MapMobileButton from '@discover-components/map/mobileButton';
import DEFAULT_ZOOM_LEVEL from '@discover-components/map/defaultZoomLevel';
import LogButton from '@discover-components/restaurant/devMenu/logButton';
import {
  hasRestaurantReviewSummary,
  RestaurantReviewSummaryDialog,
} from '@discover-components/restaurant/reviewSummary';
import Separator from '@discover-ui/separator';
import ScrollToTopButton from '@discover-ui/scrollToTopButton';
import dayjs from 'dayjs';
import useDate from '@discover-data/stores/hooks/date/useDate';
import { DATE_FORMAT_DAY_JS } from '@discover-lib/dateFormats';
import useHasActiveFilters from '@discover-data/stores/hooks/useHasActiveFilters';
import {
  NEXT_PUBLIC_DISCOVER_DEV_MENU_ENABLED,
  NEXT_PUBLIC_DISCOVER_INFINITE_SCROLL_DISABLED,
  NEXT_PUBLIC_DISCOVER_PAGE_SIZE,
} from '@/lib/env';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import useDineNowBanner from '@/components/analytics/featureFlags/dineNowBanner';
import { useLocalStorageClosedState } from '@/components/hooks/useLocalStorageClosedState';
import SilverstripeContext from '@/components/context/Silverstripe';
import { useAuthState } from '@/components/context/AuthContext';
import type { PreviewRestaurant } from '@/lib/restaurantPreview';
import RestaurantListEmptyMessage from './emptyMessage';
import DineNowBanner, { DINE_NOW_BANNER_CLOSED_STORAGE_KEY } from './dineNow/banner';
import HowItWorksCarousel from './howItWorks/carousel';
import { HOW_IT_WORKS_CLOSED_STORAGE_KEY } from './howItWorks/steps';
import { isHowItWorksVisible } from './howItWorks/visibility';
import RestaurantListItem from './item';
import RestaurantListItemContainer from './item/container';
import RestaurantListSummary from './summary';
import ListRestaurantsCarousel from '../filteredCarousel/listRestaurantCarousel';
import useInfiniteScroll from './useInfiniteScroll';
import useLocationNavigation from './useLocationNavigation';
import useMapQueryState from '../../map/useMapQueryState';

const RESTAURANTS_BETWEEN_CAROUSELS = 2;

const subscribeToHydration = () => () => undefined;
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

type RestaurantListInsertion = {
  restaurantPosition: number;
  element: ReactNode;
};

function insertAtRestaurantPositions(
  restaurantItems: ReactNode[],
  insertions: RestaurantListInsertion[]
): ReactNode[] {
  const insertionsByPosition = new Map<number, ReactNode[]>();

  insertions.forEach(({ restaurantPosition, element }) => {
    const clampedPosition = Math.min(Math.max(restaurantPosition, 0), restaurantItems.length);
    const itemsAtPosition = insertionsByPosition.get(clampedPosition) ?? [];
    itemsAtPosition.push(element);
    insertionsByPosition.set(clampedPosition, itemsAtPosition);
  });

  return [
    ...restaurantItems.flatMap((item, index) => [...(insertionsByPosition.get(index) ?? []), item]),
    ...(insertionsByPosition.get(restaurantItems.length) ?? []),
  ];
}

/**
 * Success is the settled list actually committing: at least one card, or the empty message (which only mounts after the loading debounce). The store may settle in the gap before that commit, so also listen for ready.
 */
function useReportSettledListLoad(hasPaintedResult: boolean) {
  const store = useStoreState((state) => state.deriveCacheIdentity);

  useLayoutEffect(() => {
    if (!hasPaintedResult) {
      return;
    }

    const report = () => completeListInitialLoad(store);
    report();
    return subscribeListInitialLoadReady(store, report);
  }, [hasPaintedResult, store]);
}

function clampZoom(zoom: number | null | undefined) {
  return Math.max(zoom || DEFAULT_ZOOM_LEVEL, DEFAULT_ZOOM_LEVEL); // don't zoom out further than the default
}

function getDefaultMapViewState(regionViewState: ViewState, suburb: SuburbType | null): ViewState {
  // use the selected suburb location, if available
  if (suburb && suburb.latitude != null && suburb.longitude != null) {
    const { latitude, longitude, zoom } = suburb;
    return { latitude, longitude, zoom: clampZoom(zoom) };
  }

  // otherwise default to the region location
  return {
    ...regionViewState,
    zoom: clampZoom(regionViewState.zoom),
  };
}

function useTrackRestaurantListFilterApplied(
  tags: RestaurantTagType[],
  selectedSuburbs: SuburbType[]
) {
  const { track } = useAnalytics();
  const { page } = useContext(SilverstripeContext);
  const previousFilterVariables = useRef<string>();
  const [mapOpen] = useMapQueryState();
  const rehydrated = useStoreRehydrated();
  const [filtersReady, setFiltersReady] = useState(false);

  const date = useStoreState((state) => state.date.value);
  const session = useStoreState((state) => state.session.value);
  const people = useStoreState((state) => state.people.value);
  const tagIds = useStoreState((state) => state.tagIds.value);
  const hasLastTable = useStoreState((state) => state.hasLastTable.value);
  const discountOnly = useStoreState((state) => state.discountOnly.value);
  const regularOnly = useStoreState((state) => state.regularOnly.value);
  const favourite = useStoreState((state) => state.favourite.value);

  const selectedTagLabels = useMemo(
    () =>
      tagIds
        .map((tagId) => tags.find((tag) => tag.id === tagId)?.title)
        .filter((title): title is string => !!title),
    [tagIds, tags]
  );

  const selectedSuburbNames = useMemo(
    () =>
      selectedSuburbs
        .map((suburb) => suburb.menuTitle)
        .filter((suburbName): suburbName is string => !!suburbName),
    [selectedSuburbs]
  );

  const filteredAdditionalOptions = useMemo(
    () => [
      ...(hasLastTable ? ['Last Table'] : []),
      ...(discountOnly ? ['First Table'] : []),
      ...(regularOnly ? ['Regular Table'] : []),
      ...(favourite ? ['Favourites'] : []),
    ],
    [discountOnly, favourite, hasLastTable, regularOnly]
  );

  const filterVariables = useMemo(
    () => ({
      filtered_cuisine: selectedTagLabels,
      filtered_date_time: date,
      filtered_session: session,
      filtered_suburb: selectedSuburbNames,
      filtered_additional_options: filteredAdditionalOptions,
      filtered_city_id: page?.id || undefined,
      filtered_city_name: page?.menuTitle || undefined,
      filtered_number_of_people: people,
      map_open: mapOpen,
    }),
    [
      date,
      filteredAdditionalOptions,
      mapOpen,
      page?.id,
      page?.menuTitle,
      people,
      selectedSuburbNames,
      selectedTagLabels,
      session,
    ]
  );

  useEffect(() => {
    // Let the router-to-store sync effects apply URL filters before the initial analytics event.
    if (rehydrated) {
      setFiltersReady(true);
    }
  }, [rehydrated]);

  useEffect(() => {
    if (!filtersReady) {
      return;
    }

    const serializedFilterVariables = JSON.stringify({
      ...filterVariables,
      map_open: undefined,
    });

    if (
      previousFilterVariables.current === undefined ||
      serializedFilterVariables !== previousFilterVariables.current
    ) {
      track('filter_applied', filterVariables);
      previousFilterVariables.current = serializedFilterVariables;
    }
  }, [filterVariables, filtersReady, track]);
}

export type RestaurantListProps = {
  previewRestaurants?: PreviewRestaurant[];
  regionId: number;
  regionRestaurantCount?: number | null;
  tags: RestaurantTagType[];
  suburbs: SuburbType[];
  session?: string;
  currency: string;
  regionViewState: ViewState;
  peopleOptions?: PeopleOption[] | null;
  minDiners?: PeopleOption | null;
  maxDiners?: PeopleOption | null;
  walkInsEnabled?: boolean | null;
  cityId?: string | number | null;
  cityName?: string | null;
};

export default function RestaurantList({
  previewRestaurants,
  regionId,
  regionRestaurantCount,
  tags,
  suburbs,
  session,
  currency,
  regionViewState,
  peopleOptions,
  minDiners,
  maxDiners,
  walkInsEnabled,
  cityId,
  cityName,
}: RestaurantListProps) {
  const isNavigatingLocation = useLocationNavigation();
  const { restaurants, loading } = useRestaurants({
    regionId,
    tags,
    peopleOptions,
    minDiners,
    maxDiners,
  });
  const [people] = usePeople();
  const [, setSort] = useSort();
  const [, setDate] = useDate();
  const { track } = useAnalytics();
  const { isLoggedIn, user } = useAuthState();
  const { hasNextPage } = usePage();
  const [selectedSuburbs] = useSuburbs(suburbs);
  const suburb = selectedSuburbs[0] || null;
  const [isHowItWorksDismissed, setHowItWorksDismissed] = useLocalStorageClosedState(
    HOW_IT_WORKS_CLOSED_STORAGE_KEY
  );
  const dineNowBannerEnabled = useDineNowBanner();
  const [isDineNowDismissed, setDineNowDismissed] = useLocalStorageClosedState(
    DINE_NOW_BANNER_CLOSED_STORAGE_KEY
  );
  // Before mount, the head script controls visibility using the same saved audience state.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  const hasActiveFilters = useHasActiveFilters({ tags, peopleOptions, minDiners, maxDiners });
  // Use the cached page count so the first carousel reserves space before the list request finishes.
  const showNewArrivals =
    !isNavigatingLocation &&
    !hasActiveFilters &&
    (regionRestaurantCount ?? 0) >= NEXT_PUBLIC_DISCOVER_PAGE_SIZE;
  const [processingBooking, setProcessingBooking] = useState(false);
  const [reviewSummaryRestaurantId, setReviewSummaryRestaurantId] = useState<number | null>(null);
  const defaultMapViewState = getDefaultMapViewState(regionViewState, suburb);
  useTrackRestaurantListFilterApplied(tags, selectedSuburbs);

  // debounce the state values to prevent visual jitter
  const debouncedLoading = useDebounce(loading, 200);
  const debouncedRestaurantCount = useDebounce(restaurants.length, 200);
  const hasRestaurants = restaurants.length > 0;
  const hydrated = useSyncExternalStore(subscribeToHydration, getClientSnapshot, getServerSnapshot);
  // The static preview stands in until the first list request settles. A later reset (a filter change) must not bring it back, and a location change hides it as soon as it starts.
  const storeMode = useStoreState((state) => state.mode);
  const [listSettled, setListSettled] = useState(false);
  useEffect(() => {
    // `loading` only means "first request pending" once the store is in list mode.
    if (hasRestaurants || (storeMode === 'list' && !loading)) setListSettled(true);
  }, [hasRestaurants, loading, storeMode]);
  const showRestaurantPreview =
    !!previewRestaurants?.length && !hasRestaurants && !isNavigatingLocation && !listSettled;
  const showEmptyMessage =
    !hasRestaurants &&
    debouncedRestaurantCount === 0 &&
    !loading &&
    !debouncedLoading &&
    !showRestaurantPreview &&
    !isNavigatingLocation;
  useReportSettledListLoad(!isNavigatingLocation && (hasRestaurants || showEmptyMessage));

  // observe the last item in the list to trigger loading the next page
  const observedRestaurantId: number | undefined = restaurants[restaurants.length - 1]?.id;
  const observedItemRef = useInfiniteScroll(observedRestaurantId, loading);
  const showSkeleton =
    isNavigatingLocation || ((loading || debouncedLoading) && (hasNextPage || !hasRestaurants));

  const handleNewArrivalsSeeAllClick = useCallback(() => {
    setSort('NEWEST');
  }, [setSort]);
  const handleMostPopularSeeAllClick = useCallback(() => {
    setSort('RANKING');
  }, [setSort]);
  const handleAvailableTodaySeeAllClick = useCallback(() => {
    // Carousels only render with no active filters, so default sort/session already match the available-today carousel query; the click only needs to apply today's date.
    const today = dayjs().format(DATE_FORMAT_DAY_JS);
    track('date_selected', { option_selected_id: today });
    setDate(today);
  }, [setDate, track]);
  const handleHowItWorksDismiss = useCallback(() => {
    setHowItWorksDismissed(true);
  }, [setHowItWorksDismissed]);
  const handleDineNowDismiss = useCallback(() => {
    setDineNowDismissed(true);
  }, [setDineNowDismissed]);

  const buildItems = () => {
    const visibleRestaurants = isNavigatingLocation ? [] : restaurants;
    const restaurantItems = visibleRestaurants.map((restaurant, index) => (
      <Fragment key={restaurant.id}>
        {index !== 0 && <Separator className="min-w-0 lg:mr-10" />}
        <RestaurantListItemContainer
          className={cn({
            relative: NEXT_PUBLIC_DISCOVER_DEV_MENU_ENABLED,
          })}
          id={index === restaurants.length - 1 ? 'last-item' : undefined}>
          <RestaurantListItem
            restaurant={restaurant}
            index={index}
            imagePriority={index < 2}
            processingBooking={processingBooking}
            setProcessingBooking={setProcessingBooking}
            onOpenReviewSummary={setReviewSummaryRestaurantId}
            ref={
              !NEXT_PUBLIC_DISCOVER_INFINITE_SCROLL_DISABLED &&
              observedRestaurantId === restaurant.id
                ? observedItemRef
                : undefined
            }
          />

          {NEXT_PUBLIC_DISCOVER_DEV_MENU_ENABLED && (
            <LogButton
              name={restaurant.menuTitle || 'Log'}
              data={restaurant}
              className="absolute right-3 top-16 lg:right-10"
            />
          )}
        </RestaurantListItemContainer>
      </Fragment>
    ));
    const insertions: RestaurantListInsertion[] = [];

    if (showNewArrivals) {
      insertions.push({
        restaurantPosition: 0,
        element: (
          <li key="carousel-new-arrivals" className="list-none">
            <ListRestaurantsCarousel
              regionId={regionId}
              mode="newArrivals"
              preserveSpace
              enabled={hydrated}
              onSeeAllClick={handleNewArrivalsSeeAllClick}
              className="mt-3"
            />
          </li>
        ),
      });
    }

    if (
      !isNavigatingLocation &&
      !hasActiveFilters &&
      restaurants.length >= NEXT_PUBLIC_DISCOVER_PAGE_SIZE
    ) {
      insertions.push(
        {
          restaurantPosition: RESTAURANTS_BETWEEN_CAROUSELS,
          element: (
            <li key="carousel-most-popular" className="list-none">
              <ListRestaurantsCarousel
                regionId={regionId}
                mode="mostPopular"
                onSeeAllClick={handleMostPopularSeeAllClick}
              />
            </li>
          ),
        },
        {
          restaurantPosition: RESTAURANTS_BETWEEN_CAROUSELS * 2,
          element: (
            <li key="carousel-available-today" className="list-none">
              <ListRestaurantsCarousel
                regionId={regionId}
                mode="availableToday"
                onSeeAllClick={handleAvailableTodaySeeAllClick}
              />
            </li>
          ),
        }
      );
    }

    const items = insertAtRestaurantPositions(restaurantItems, insertions);

    if (showSkeleton || showRestaurantPreview) {
      items.push(
        <Fragment key="skeleton">
          {visibleRestaurants.length !== 0 && <Separator className="mr-10 min-w-0" />}
          <RestaurantListItemContainer>
            <RestaurantCardSkeleton discount={0.5} />
          </RestaurantListItemContainer>
        </Fragment>
      );
    }

    if (showRestaurantPreview)
      items.push(<RestaurantPreview key="restaurant-preview" restaurants={previewRestaurants} />);

    return items;
  };

  // handle selecting a slot
  const [selectedSlot, setSelectedSlot] =
    useState<RegularAvailabilitySlotWithNonEmptyAvailabilityOptions | null>(null);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(null);
  const setSelected = useCallback(
    (newValues: {
      selectedSlot: RegularAvailabilitySlotWithNonEmptyAvailabilityOptions | null;
      selectedRestaurantId: number | null;
    }) => {
      setSelectedSlot(newValues.selectedSlot);
      setSelectedRestaurantId(newValues.selectedRestaurantId);
    },
    [setSelectedSlot, setSelectedRestaurantId]
  );
  const contextValue = useMemo(
    () => ({
      selectedSlot,
      selectedRestaurantId,
      setSelected,
    }),
    [selectedSlot, selectedRestaurantId, setSelected]
  );
  const selectedRestaurant = restaurants.find(({ id }) => id === selectedRestaurantId);
  const shouldShowList = hasRestaurants || showSkeleton || showRestaurantPreview || showNewArrivals;
  const shouldShowHowItWorks = isHowItWorksVisible(
    isHowItWorksDismissed,
    isLoggedIn || user != null,
    user?.confirmedBookingsCount
  );
  let howItWorksVisibility = 'pending';
  if (hasMounted) howItWorksVisibility = shouldShowHowItWorks ? 'show' : 'hide';
  // Wait until dismiss storage has been read. The flag can be forced on the first client render via its query param, and showing before that read flashes a dismissed banner.
  const showDineNowBanner =
    hasMounted && dineNowBannerEnabled && walkInsEnabled === true && !isDineNowDismissed;
  const reviewSummaryRestaurant = restaurants.find(({ id }) => id === reviewSummaryRestaurantId);

  const resultsRef = useRef<HTMLDivElement>(null);
  const prevRestaurantsLength = usePrevious(restaurants.length);
  useEffect(() => {
    if (!resultsRef.current || !prevRestaurantsLength || restaurants.length !== 0) return;

    const filterBars = document.querySelectorAll<HTMLElement>('[data-restaurant-filter-bar]');
    const inset = Math.max(
      0,
      ...Array.from(filterBars, (bar) =>
        bar.offsetHeight ? bar.offsetHeight + (parseFloat(getComputedStyle(bar).top) || 0) : 0
      )
    );
    const { top } = resultsRef.current.getBoundingClientRect();
    if (top >= inset && top < window.innerHeight) return;

    window.scrollTo({
      top: window.scrollY + top - inset - 16,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    });
  }, [restaurants.length, prevRestaurantsLength]);

  return (
    <>
      <RestaurantFiltersDesktopTopBar
        session={session}
        peopleOptions={peopleOptions}
        minDiners={minDiners}
        maxDiners={maxDiners}
      />
      <RestaurantFiltersMobileTopBar
        tags={tags}
        suburbs={suburbs}
        peopleOptions={peopleOptions}
        minDiners={minDiners}
        maxDiners={maxDiners}
      />
      <div className="mt-2 flex flex-row lg:container lg:mt-10 lg:px-0">
        <div
          ref={resultsRef}
          id="restaurant-results"
          className="w-full max-w-full pb-12 lg:max-w-[75%]">
          <AvailabilityOptionsDialogContext.Provider value={contextValue}>
            {shouldShowList && (
              <>
                <RestaurantListSummary className="px-3 lg:pl-0 lg:pr-10" />
                {showDineNowBanner ? (
                  <DineNowBanner
                    cityId={cityId}
                    cityName={cityName}
                    onDismiss={handleDineNowDismiss}
                  />
                ) : (
                  <div data-how-it-works-visibility={howItWorksVisibility}>
                    <HowItWorksCarousel onDismiss={handleHowItWorksDismiss} />
                  </div>
                )}
                <ol
                  aria-busy={isNavigatingLocation || loading}
                  className="m-0 flex max-w-full list-none flex-col items-stretch p-0">
                  {buildItems()}
                </ol>
              </>
            )}
            {showEmptyMessage && (
              <RestaurantListEmptyMessage
                tags={tags}
                peopleOptions={peopleOptions}
                minDiners={minDiners}
                maxDiners={maxDiners}
              />
            )}
            {!!selectedRestaurant && (
              <AvailabilityOptionsDialogWithBooking
                currency={currency}
                restaurant={selectedRestaurant}
              />
            )}
            {reviewSummaryRestaurant &&
              hasRestaurantReviewSummary(reviewSummaryRestaurant) &&
              reviewSummaryRestaurant.rating != null &&
              reviewSummaryRestaurant.approvedReviewsCount != null && (
                <RestaurantReviewSummaryDialog
                  open={reviewSummaryRestaurantId === reviewSummaryRestaurant.id}
                  onOpenChange={(open) => {
                    if (!open) {
                      setReviewSummaryRestaurantId(null);
                    }
                  }}
                  menuTitle={reviewSummaryRestaurant.menuTitle || ''}
                  slug={reviewSummaryRestaurant.slug}
                  people={people}
                  rating={reviewSummaryRestaurant.rating}
                  approvedReviewsCount={reviewSummaryRestaurant.approvedReviewsCount}
                  reviewSummary={reviewSummaryRestaurant.reviewSummary}
                  reviewSummaryDetails={reviewSummaryRestaurant.reviewSummaryDetails}
                />
              )}
          </AvailabilityOptionsDialogContext.Provider>
        </div>
        <DesktopSideBar
          defaultMapViewState={defaultMapViewState}
          tags={tags}
          suburbs={suburbs}
          peopleOptions={peopleOptions}
          minDiners={minDiners}
          maxDiners={maxDiners}
        />
      </div>
      <div className="hidden lg:contents">
        <ScrollToTopButton className="fixed bottom-[calc(2rem+var(--safe-area-inset-bottom))] right-8" />
      </div>
      <ScrollToTopButton className="fixed bottom-[calc(1rem+var(--safe-area-inset-bottom))] right-4 lg:hidden" />
      <MapMobileButton
        kind="outline"
        className="fixed bottom-[calc(1rem+var(--safe-area-inset-bottom))] left-1/2 z-10 -translate-x-1/2 shadow lg:hidden"
        round
        defaultViewState={defaultMapViewState}
        tags={tags}
        suburbs={suburbs}
        peopleOptions={peopleOptions}
        minDiners={minDiners}
        maxDiners={maxDiners}
      />
    </>
  );
}
